import React from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {parseText, isMobile} from "../../utils/utils";
import AssetLabel, { isStockAsset } from "../common/AssetLabel";
import RangeExtremumBadge, { rangeExtremumDescription } from "../common/RangeExtremumBadge";
import aiSupportsIcon from "../../assets/ai-indicator/cryptember-ai-supports.svg";
import aiNeutralIcon from "../../assets/ai-indicator/cryptember-ai-neutral.svg";
import aiConflictsIcon from "../../assets/ai-indicator/cryptember-ai-conflicts.svg";

const AI_ASSESSMENT_ICONS = {
  SUPPORTS: aiSupportsIcon,
  NEUTRAL: aiNeutralIcon,
  CONFLICTS: aiConflictsIcon,
};

function EventButton({ event, openPopup }) {
  const getVariant = () => {
    if (event) {
      if (event.bull && !event.bear) {
        return "success";
      } else if (!event.bull && event.bear) {
        return  "danger";
      } else {
        return "primary";
      }
    } else {
      return "primary";
    }
  }

  const getTitle = () => {
      try {
          if (event) {
              return parseText(event.name);
          } else {
              return "Currency Details";
          }
      } catch (e) {
          return event?.name || event?.cur || "Currency Details";
      }
  }

  const popupData = () => {
    return event;
  }

  const getBtnClass = () => {
    return isMobile() ? "eventButtonMobile" : "eventButtonDesktop"
  }

  const openCurrencyPopup = (event) => {
      event.stopPropagation();
      openPopup(popupData());
  }

  const confirmations = Array.isArray(event?.sr)
    ? event.sr.filter((item) => item != null && String(item).trim())
    : event?.sr != null && String(event.sr).trim() ? [event.sr] : [];

  const renderConfirmationIndicator = () => {
    if (confirmations.length === 0) return null;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`confirmations-${event.id || event.cur || 'event'}`}>
            <strong>Confirmations</strong><br />
            <ul style={{ margin: '5px 0', paddingLeft: '15px' }}>
              {confirmations.map((item, index) => (
                <li key={index} style={{ margin: '2px 0' }}>{item}</li>
              ))}
            </ul>
          </Tooltip>
        }>
        <span
          className="eventIndicator eventConfirmationIndicator"
          onClick={openCurrencyPopup}
          aria-label="Confirmations"
          role="img">
          ★
        </span>
      </OverlayTrigger>
    );
  };

  const renderAiAssessmentIndicator = () => {
    const evaluation = event?.codexEvaluation;
    if (evaluation) {
      const rawConclusion = evaluation.eventAlignment || evaluation.verdict || "NEUTRAL";
      const conclusion = String(rawConclusion).toUpperCase();
      const assessmentIcon = AI_ASSESSMENT_ICONS[conclusion] || aiNeutralIcon;
      const details = [
        evaluation.responseMode === "REFERENCE_V1" ? "Reference comparison" : null,
        evaluation.successScore != null ? `Successful similarity ${evaluation.successScore}/100` : null,
        evaluation.failureScore != null ? `Failed similarity ${evaluation.failureScore}/100` : null,
        evaluation.score != null ? `Score ${evaluation.score}` : null,
        evaluation.confidence != null ? `Confidence ${evaluation.confidence}%` : null,
        evaluation.timing || null,
        evaluation.trend || null,
        evaluation.structure || null,
      ].filter(Boolean).join(" · ");
      const supportingPoints = [
        ...(Array.isArray(evaluation.strengths) ? evaluation.strengths : []),
        ...(Array.isArray(evaluation.risks) ? evaluation.risks : []),
        ...(Array.isArray(evaluation.evidence) ? evaluation.evidence : []),
      ];
      return (
          <OverlayTrigger
           placement="top"
           overlay={
             <Tooltip id={`tooltip-${event.id || 'star'}`}>
               <strong>{conclusion}</strong>
               {details ? <><br /><span>{details}</span></> : null}
               {evaluation.summary ? <><br /><span>{evaluation.summary}</span></> : null}
               {supportingPoints.length > 0 ? (
                 <ul style={{ margin: '5px 0', paddingLeft: '15px' }}>
                   {supportingPoints.map((item, index) => (
                     <li key={index} style={{ margin: '2px 0' }}>{item}</li>
                   ))}
                 </ul>
               ) : null}
             </Tooltip>
             }>
             <span
               className="eventIndicator eventAiAssessmentIndicator"
               onClick={openCurrencyPopup}
               aria-label="Automatic AI assessment">
               <img
                 src={assessmentIcon}
                 alt={`Automatic AI assessment: ${conclusion}`}
                 className="eventAiAssessmentIcon"
               />
             </span>
        </OverlayTrigger>
      );
    }
    return null;
  };

  const button = (
    <Button
      id="eventButton"
      className={`${getBtnClass()}${isStockAsset(event) ? " eventButtonWithAssetBadge" : ""}${rangeExtremumDescription(event?.rangeExtremum) ? " eventButtonWithRangeBadge" : ""}`}
      variant={getVariant()}
      title={[getTitle(), rangeExtremumDescription(event?.rangeExtremum)].filter(Boolean).join(" · ")}
      onClick={openCurrencyPopup}>
      <AssetLabel event={event} symbol={event?.cur} className="assetLabelCompact" />
      <RangeExtremumBadge evidence={event?.rangeExtremum} />
    </Button>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {(confirmations.length > 0 || event?.codexEvaluation) && (
        <span className="eventIndicators" role="group" aria-label="Event indicators">
          {renderConfirmationIndicator()}
          {renderAiAssessmentIndicator()}
        </span>
      )}
      {button}
    </div>
  );
}

export default React.memo(EventButton);
